import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Menu from '../Menu/Menu';
import '../../styles/core.css';
import { useAuth } from '../../auth/AuthContext';
import api from '../../utils/axios.secure';

const columns = [
  ['idx', '번호'],
  ['user_id', '아동 번호'],
  ['content_name', '콘텐츠'],
  ['gameMode', '모드'],
  ['playTime', '수행 시간'],
  ['correctCount', '정반응'],
  ['missCount', '누락'],
  ['wrongCount', '오반응'],
  ['score', '점수'],
  ['accuracy', '정확도'],
  ['reactionTime', '반응 시간'],
  ['correctTime', '정반응 시간'],
  ['extraData', '추가 데이터'],
  ['saveDate', '플레이 시간'],
];

const keys = columns.map(([key]) => key);

function unwrapRows(res) {
  const payload = res?.data || res;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload)) return payload;
  return [];
}

function valueOf(row, key, fallback = '-') {
  const value = row?.[key];
  return value === null || value === undefined || value === '' ? fallback : value;
}

function toNumber(value) {
  const n = Number(String(value ?? '').replace('%', ''));
  return Number.isFinite(n) ? n : null;
}

function average(rows, key) {
  const nums = rows.map((row) => toNumber(row[key])).filter((n) => n !== null);
  if (!nums.length) return '-';
  return Math.round(nums.reduce((sum, n) => sum + n, 0) / nums.length);
}

function Contentresult() {
  const [playdata, setPlaydata] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [loadMessage, setLoadMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const updatePlaydata = useCallback(async () => {
    setLoading(true);
    try {
      const role = String(user?.role || '').toUpperCase();
      const matching =
        user?.matching ??
        user?.clinic_id ??
        user?.manager_id ??
        user?.admin_id ??
        user?.id ??
        null;
      const body = role === 'ADMIN' || !matching ? {} : { matching };

      const { data: res } = await api.post('/manager/content/contentResult', body);
      console.log('[contentResult request]', body);
      console.log('[contentResult response]', res);

      const rows = unwrapRows(res);
      setPlaydata(rows);
      setLoadMessage(rows.length ? '' : '조회된 콘텐츠 수행 결과가 없습니다.');
    } catch (err) {
      console.error('Error fetching content result:', err);
      console.error('contentResult response:', err?.response?.data);
      setPlaydata([]);
      setLoadMessage(
        err?.response?.data?.message || '콘텐츠 수행 결과 조회 중 서버 오류가 발생했습니다.'
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    updatePlaydata();
  }, [updatePlaydata]);

  const filteredRows = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return playdata;
    return playdata.filter((row) =>
      keys.some((key) => String(row?.[key] ?? '').toLowerCase().includes(query))
    );
  }, [filterText, playdata]);

  const stats = useMemo(() => {
    const uniqueChildren = new Set(playdata.map((row) => row.user_id).filter(Boolean)).size;
    return [
      { label: '전체 기록', value: playdata.length.toLocaleString(), hint: '수행 결과', tone: 'blue' },
      {
        label: '대상 아동',
        value: uniqueChildren.toLocaleString(),
        hint: '고유 아동',
        tone: 'teal',
      },
      { label: '평균 점수', value: average(playdata, 'score'), hint: 'score 기준', tone: 'amber' },
      {
        label: '평균 정확도',
        value: average(playdata, 'accuracy'),
        hint: 'accuracy 기준',
        tone: 'green',
      },
    ];
  }, [playdata]);

  const exportCsv = () => {
    const header = columns.map(([, label]) => label).join(',');
    const lines = filteredRows.map((row) =>
      columns
        .map(([key]) => `"${String(row?.[key] ?? '').replace(/"/g, '""')}"`)
        .join(',')
    );
    const blob = new Blob(['\uFEFF' + [header, ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'content-result.csv');
    link.click();
    URL.revokeObjectURL(url);
  };

  const openDetail = (row) => {
    const index = row?.idx;
    if (index !== null && index !== undefined && index !== '') {
      navigate(`/dcr/${index}`);
    }
  };

  return (
    <Menu>
      <section className="screen content-result-screen">
        <div className="page-tabs" aria-label="콘텐츠 결과 보기 옵션">
          <button type="button" className="tab active">
            수행 결과
          </button>
          <button type="button" className="tab" disabled>
            상세 분석
          </button>
          <button type="button" className="tab" disabled>
            리포트
          </button>
        </div>

        <div className="screen-head">
          <div>
            <p className="eyebrow">Analysis</p>
            <h1 className="screen-title">콘텐츠 수행 결과</h1>
            <p className="screen-copy">아동별 수행 기록과 반응 데이터를 확인합니다.</p>
          </div>
          <div className="screen-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={updatePlaydata}
              disabled={loading}
            >
              새로고침
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={exportCsv}
              disabled={!filteredRows.length}
            >
              내보내기
            </button>
          </div>
        </div>

        <div className="kpi-grid">
          {stats.map((item) => (
            <div className="kpi-card" key={item.label}>
              <span className={`kpi-accent ${item.tone}`} />
              <p className="kpi-label">{item.label}</p>
              <strong className="kpi-value">{item.value}</strong>
              <div className="kpi-meta">
                <span className="kpi-period">{item.hint}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="card content-table-card">
          <div className="card-header">
            <div>
              <h2 className="card-title">수행 기록</h2>
              <p className="card-subtitle">
                총 {playdata.length.toLocaleString()}건 중 {filteredRows.length.toLocaleString()}건 표시
              </p>
            </div>
            {loading && <span className="badge badge-blue">불러오는 중</span>}
          </div>

          <div className="search-row">
            <input
              className="search-input"
              type="text"
              placeholder="아동 번호, 콘텐츠, 점수로 검색"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>

          {loadMessage && !loading && <div className="notice">{loadMessage}</div>}

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map(([, label]) => (
                    <th key={label}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, index) => (
                  <tr
                    className="clickable"
                    key={`${valueOf(row, 'idx', index)}-${index}`}
                    onClick={() => openDetail(row)}
                  >
                    {columns.map(([key]) => (
                      <td key={key}>{valueOf(row, key)}</td>
                    ))}
                  </tr>
                ))}
                {!filteredRows.length && (
                  <tr>
                    <td className="empty-cell" colSpan={columns.length}>
                      표시할 데이터가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="table-footer">
            <span>행을 클릭하면 상세 결과로 이동합니다.</span>
            <span>{filteredRows.length.toLocaleString()} rows</span>
          </div>
        </div>
      </section>
    </Menu>
  );
}

export default Contentresult;
